import { MessageSquarePlus, Trash2, MessageCircle, Settings, PanelLeftClose, LogOut, User, Target, Sun, Moon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/components/ThemeContext";

export default function Sidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  view,
  onViewChange,
  isOpen,
  onToggle,
  user,
  onLogout,
  isAdmin,
}) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  
  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={onToggle}
          />
        )}
      </AnimatePresence>

      <motion.aside
        data-testid="sidebar"
        initial={false}
        animate={{ width: isOpen ? 288 : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className={`fixed lg:relative z-40 h-full flex flex-col overflow-hidden ${
          isOpen ? "w-72" : "w-0"
        }`}
        style={{ backgroundColor: "var(--bg-main)", borderRight: "1px solid var(--border-main)" }}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between flex-shrink-0" style={{ borderBottom: "1px solid var(--border-main)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <span className="text-emerald-400 font-bold text-sm" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>E</span>
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: "var(--text-primary)" }}>
                Edgar IA
              </h1>
              <p className="text-[10px] tracking-wider uppercase" style={{ color: "var(--text-muted)" }}>Agente 360</p>
            </div>
          </div>
          <button
            data-testid="close-sidebar-btn"
            onClick={onToggle}
            className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>

        {/* New Chat */}
        <div className="p-3 flex-shrink-0">
          <button
            data-testid="new-chat-btn"
            onClick={onNewChat}
            className="w-full flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-4 py-2.5 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgba(16,185,129,0.2)]"
          >
            <MessageSquarePlus size={16} />
            Nueva conversación
          </button>
        </div>

        {/* Nav Tabs */}
        <div className="px-3 flex gap-1 flex-shrink-0">
          <button
            data-testid="nav-chat-btn"
            onClick={() => onViewChange("chat")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              view === "chat"
                ? "bg-zinc-800 text-zinc-50"
                : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900"
            }`}
          >
            <MessageCircle size={14} />
            Chat
          </button>
          <button
            data-testid="nav-plans-btn"
            onClick={() => onViewChange("plans")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              view === "plans"
                ? "bg-zinc-800 text-zinc-50"
                : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900"
            }`}
          >
            <Target size={14} />
            Planes
          </button>
          {isAdmin && (
            <button
              data-testid="nav-admin-btn"
              onClick={() => onViewChange("admin")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                view === "admin"
                  ? "bg-zinc-800 text-zinc-50"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900"
              }`}
            >
              <Settings size={14} />
              Admin
            </button>
          )}
        </div>

        {/* Sessions list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1 min-h-0">
          {sessions.length === 0 && (
            <p className="text-xs text-zinc-600 text-center mt-8 px-4">
              Sin conversaciones aún. Inicia una nueva.
            </p>
          )}
          {sessions.map((session) => (
            <div
              key={session.id}
              data-testid={`session-item-${session.id}`}
              onClick={() => onSelectSession(session.id)}
              className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-sm ${
                activeSessionId === session.id
                  ? "bg-zinc-800/80 text-zinc-50 border border-zinc-700/50"
                  : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
              }`}
            >
              <MessageCircle size={14} className="flex-shrink-0 text-zinc-500" />
              <span className="truncate flex-1">{session.title}</span>
              <button
                data-testid={`delete-session-${session.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteSession(session.id);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-zinc-700 text-zinc-500 hover:text-red-400 transition-all"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        {/* User + Theme + Logout */}
        <div className="p-3 flex-shrink-0" style={{ borderTop: "1px solid var(--border-main)" }}>
          {user && (
            <div className="flex items-center gap-2.5 mb-2 px-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--bg-surface-hover, var(--bg-bubble-user))" }}>
                <User size={13} style={{ color: "var(--text-muted)" }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate" style={{ color: "var(--text-secondary)" }}>{user.name || user.email}</p>
                <p className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-faint)" }}>
                  {user.role === "admin" ? "Administrador" : "Alumno"}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-1">
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="flex-1 flex items-center gap-2 px-4 py-2 rounded-lg text-xs transition-all"
              style={{ color: "var(--text-muted)" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-surface-hover)"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
            >
              {isDark ? <Sun size={13} /> : <Moon size={13} />}
              {isDark ? "Modo claro" : "Modo oscuro"}
            </button>
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs transition-all"
              style={{ color: "var(--text-muted)" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-surface-hover)"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
            >
              <LogOut size={13} />
              Salir
            </button>
          </div>
        </div>
      </motion.aside>
    </>
  );
}
